// Libs
import { useContext } from 'react'
import { useAsyncSetState, useGetState } from 'use-async-setstate'
import { useTranslation } from 'next-i18next'

// Comps
import {
  NookEvent,
  FetchEventsParams,
  FetchEventsResult,
  ToastType,
} from '../types'
import { FORMATTER } from '../utils/formatter'
import { API } from '../API'
import { ToastContext } from '../context'

// Deffs
type useSearchableEventListHook = (initialFilters?: FetchEventsParams) => {
  // State
  events: NookEvent[]
  hasNextPage: boolean

  // Mutators
  getFirstPage: () => Promise<void>
  getNextPage: () => Promise<void>
  changeFilters: (newFilters: FetchEventsParams) => Promise<void>
}

export const useSearchableEventList: useSearchableEventListHook = (
  initialFilters,
) => {
  // Global State
  const toastContext = useContext(ToastContext)

  const { t } = useTranslation('common')

  // Local State
  const [events, setEvents] = useAsyncSetState<NookEvent[]>([])
  const getCurrentEvents = useGetState(events)
  const [filters, setFilters] = useAsyncSetState<FetchEventsParams>({
    // Set defaults
    ...{
      page: 1,
      category: 'allCategory',
      search: '',
    },
    // Anything else from the initialFilters
    ...(initialFilters || {}),
  })
  const getCurrentFilters = useGetState(filters)

  // Computed
  const hasNextPage = filters?.page && filters?.page > 0 ? true : false

  // Funcs
  const fetchEvents = async () => {
    // Filters
    // - some are un-used or for future use
    // will pull what they currently are
    const {
      category,
      search,
      page,
      ordering,
      recommended,
      event_not_in,
      paid_type,
      reset,
      sticky,
      participating,
    } = getCurrentFilters() || {}

    // Set some defaults - pulled from previous behavior
    const defaultCategory = 'allCategory'
    const requestCategory = category === defaultCategory ? undefined : category
    const requestPage = page || 1

    // If we are above the first page or not -1 (no more pages)
    if (requestPage > 0) {
      // Build Get Request
      const querySet = {
        category: requestCategory,
        search: search || undefined,
        page: requestPage,
        recommended: recommended || false,
        event_not_in: event_not_in || undefined,
        paid_type: paid_type || undefined,
        sticky: sticky || false,
        participating: participating ?? false,
      }
      try {
        const path = `/events/${FORMATTER.objectToQueryString({
          ...querySet,
        })}`

        // Get and handle response
        const response = await API.get<FetchEventsResult>(path)
        const { results = null, next = null, previous = null } = response.data

        if (results !== null) {
          // Filter out new events and add new events into array
          const newEvents = []
          const currentEvents = getCurrentEvents()
          results.forEach((e) => {
            if (!events.some((f) => f.id === e.id)) {
              newEvents.push(e)
            }
          })
          setEvents([...currentEvents, ...results])
        }
        // If more pages update the page
        const currentFilters = getCurrentFilters()
        if (next !== null) {
          setFilters({
            ...currentFilters,
            page: currentFilters.page + 1,
          })
          // Else turn into -1 to prevent load more
        } else {
          setFilters({
            ...currentFilters,
            page: -1,
          })
        }
      } catch (e) {
        console.warn(
          'tried to get page with filters CODE:EVENTS_A',
          filters,
          getCurrentFilters(),
        )
        toastContext.addToast({
          id: 'events get error a',
          toastType: ToastType.Error,
          title: t(
            `Oops, Something went wrong.`,
            `Oops, Something went wrong.`,
          ),
          content: t(
            `We were unable to get the events. Please try again.`,
            `We were unable to get the events. Please try again.`,
          ),
        })
      }
    } else {
      console.warn(
        'tried to get next page with filters CODE:EVENTS_B',
        filters,
        getCurrentFilters(),
      )
      toastContext.addToast({
        id: 'events get error b',
        toastType: ToastType.Error,
        title: t(`Oops, Something went wrong.`, `Oops, Something went wrong.`),
        content: t(
          `We were unable to get the events. Please try again.`,
          `We were unable to get the events. Please try again.`,
        ),
      })
    }
  }

  // Get the first page and re-set the events lists
  const getFirstPage = async () => {
    // Re-set page
    await setFilters({
      ...getCurrentFilters(),
      page: 1,
    })
    // Reset and get first page
    await setEvents([])
    await fetchEvents()
  }

  // Get the next page
  const getNextPage = async () => {
    await fetchEvents()
  }

  // Update filters
  // Not tested yet
  const changeFilters = async (newFilters) => {
    setFilters({
      ...getCurrentFilters(),
      ...newFilters,
    })
  }

  // vars and functions
  return {
    // State
    events,
    hasNextPage,

    // Mutators
    getFirstPage,
    getNextPage,
    changeFilters,
  }
}
